/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpStatusCode
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, throwError } from 'rxjs';
import { catchError, concatMap, map, retry } from 'rxjs/operators';

import { getRemoteError } from '@app/core/errors/helpers/get-remote-error';
import { shouldRetry } from '@app/shared/rxjs/custom-operators';

import {
  LivSuccessResponse,
  LivErrorResponse,
  LivResponseProtocol
} from '../models/liv-response-protocol.model';

const EXCLUDED_STATUS_CODES_ON_RETRY = [
  HttpStatusCode.NotFound,
  HttpStatusCode.Unauthorized,
  HttpStatusCode.UnprocessableEntity
];

const retryConfig = {
  maxRetryAttempts: 1,
  scalingDuration: 3000,
  excludedStatusCodes: EXCLUDED_STATUS_CODES_ON_RETRY
} as const;

@Injectable()
export class ResponseProtocolInterceptor implements HttpInterceptor {
  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<LivSuccessResponse | LivErrorResponse>> {
    return next.handle(req).pipe(
      concatMap((event) => this.processHttpEvent(event)),
      map((event) => this.handleResponseEvent(event)),
      retry(shouldRetry(retryConfig)),
      catchError((err: HttpErrorResponse) => this.handleErrorWithFallback(err))
    );
  }

  private processHttpEvent(
    event: HttpEvent<unknown>
  ): Promise<HttpEvent<unknown>> {
    if (event instanceof HttpResponse) {
      return this.handleJsonResponseAsBlob(event);
    }
    return Promise.resolve(event);
  }

  private handleResponseEvent(
    event: HttpEvent<unknown>
  ): HttpEvent<LivSuccessResponse | LivErrorResponse> {
    if (event instanceof HttpResponse) {
      return event.clone({ body: this.processResponse(event) });
    }
    return event;
  }

  private processResponse(
    event: HttpResponse<unknown>
  ): LivSuccessResponse | LivErrorResponse {
    const response = event.body as LivResponseProtocol;

    if (
      event.url.includes('assets') ||
      event.url.includes('google') ||
      event.body instanceof Blob
    ) {
      return event.body as never;
    }

    if (response.error) {
      throw new HttpErrorResponse({
        status: response.status ?? event.status,
        error: response.error,
        url: event.url
      });
    }

    if ('data' in response) {
      return {
        status: response.status ?? event.status,
        data: response.data,
        meta: response.meta
      };
    }

    throw new HttpErrorResponse({
      status: event.status,
      error: event.body,
      url: event.url
    });
  }

  private handleErrorWithFallback(
    response: HttpErrorResponse
  ): Observable<HttpEvent<LivSuccessResponse | LivErrorResponse>> {
    return throwError(() => this.createErrorResponse(response));
  }

  private createErrorResponse(response: HttpErrorResponse): LivErrorResponse {
    const remote = getRemoteError(response);

    return {
      status: remote.status || response.status,
      error: {
        message: remote.message,
        severity: remote.severity,
        hasApiMessage: remote.hasApiMessage,
        stack: {
          url: response.url ?? ''
        }
      }
    };
  }

  private async handleJsonResponseAsBlob(event: HttpResponse<unknown>) {
    if (event.body instanceof Blob && event.body.type === 'application/json') {
      const content = await event.body.text();
      return event.clone({ body: JSON.parse(content) });
    }
    return event;
  }
}
